import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Trash2, Info, AlertTriangle } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import OracleConnectionPanel from "./OracleConnectionPanel";

/**
 * Live-tenant Connections panel — Platinum-only.
 *
 * Two providers:
 *   • Microsoft Dynamics 365 (Azure AD app · Dataverse Web API)
 *   • SAP S/4HANA (OData endpoint · basic auth)
 *
 * Credentials are POSTed to the backend and stored in `csuite_connections`.
 * Secrets are never returned to the client — the GET only reveals which
 * fields are populated + last test result. "Test connection" hits a
 * probe endpoint that runs the OAuth (or basic-auth ping) without saving.
 */
export default function ConnectionsPanel() {
  const [state, setState] = useState({ loading: true, conn: null, err: "" });
  const reload = () => {
    setState(s => ({ ...s, loading: true }));
    csuiteApi.connections()
      .then(conn => setState({ loading: false, conn, err: "" }))
      .catch(e => setState({ loading: false, conn: null, err: e.message || "Failed" }));
  };
  useEffect(reload, []);

  if (state.loading) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin"/></div>;
  if (state.err && !state.conn) return <div className="p-6 text-red-700 flex items-center gap-2"><AlertTriangle size={16}/>{state.err}</div>;

  return (
    <div className="space-y-6" data-testid="platinum-connections">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Connections</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Live tenant integrations</h2>
        <div className="text-sm text-zinc-600 mt-1 flex items-start gap-1.5 max-w-2xl">
          <Info size={13} className="mt-0.5 shrink-0"/>
          <span>Wire your Microsoft Dynamics 365, SAP S/4HANA or Oracle Fusion Cloud tenant so the cockpits pull real data. Until then, cockpits show presentation-grade mock data. Secrets are stored server-side and never sent back to your browser.</span>
        </div>
      </div>

      <D365Card conn={state.conn.d365} reload={reload} />
      <HanaCard conn={state.conn.hana} reload={reload} />
      <OracleConnectionPanel />
    </div>
  );
}

function StatusBadge({ conn }) {
  if (!conn?.connected) return <span className="pill pill-neutral text-[10px]" data-testid="conn-status-disconnected">Not connected</span>;
  const isOk = (conn?.last_status || "ok") === "ok";
  return <span className={`pill text-[10px] ${isOk ? "pill-green" : "pill-red"}`} data-testid="conn-status-connected">{isOk ? "Connected · live" : "Configured · error"}</span>;
}

function D365Card({ conn, reload }) {
  const [f, setF] = useState({
    tenant_id: conn.tenant_id || "",
    client_id: conn.client_id || "",
    client_secret: "",
    org_url: conn.org_url || "",
  });
  const [busy, setBusy] = useState("");
  const [probe, setProbe] = useState(null);

  const save = async () => {
    if (!f.tenant_id || !f.client_id || !f.client_secret || !f.org_url) return;
    setBusy("save"); setProbe(null);
    try { await csuiteApi.saveD365Config(f); await reload(); }
    catch (e) { setProbe({ ok: false, error: e.message }); }
    finally { setBusy(""); }
  };
  const test = async () => {
    if (!f.tenant_id || !f.client_id || !f.client_secret || !f.org_url) {
      setProbe({ ok: false, error: "All four fields are required." });
      return;
    }
    setBusy("test"); setProbe(null);
    try { const r = await csuiteApi.testD365(f); setProbe(r); }
    catch (e) { setProbe({ ok: false, error: e.message }); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm("Disconnect the D365 tenant? Cockpit will fall back to mock data.")) return;
    setBusy("delete");
    try { await csuiteApi.deleteConnection("d365"); setF({ tenant_id:"", client_id:"", client_secret:"", org_url:"" }); await reload(); }
    finally { setBusy(""); }
  };

  return (
    <div className="card p-5" data-testid="conn-d365-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Microsoft Dynamics 365</div>
          <div className="font-display text-lg font-extrabold">Azure AD · Dataverse Web API</div>
          <div className="text-xs text-zinc-500 mt-1">Register an <b>Azure AD app</b> with the Dataverse <code>user_impersonation</code> permission. Grant admin consent. Provide the app&rsquo;s tenant, client ID, and secret.</div>
        </div>
        <StatusBadge conn={conn} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Tenant ID (GUID)"    value={f.tenant_id}     onChange={v => setF({ ...f, tenant_id: v })}    placeholder="00000000-0000-0000-0000-000000000000" testid="conn-d365-tenant"/>
        <Field label="Client (App) ID"     value={f.client_id}     onChange={v => setF({ ...f, client_id: v })}    placeholder="00000000-0000-0000-0000-000000000000" testid="conn-d365-client"/>
        <Field label="Client secret"       value={f.client_secret} onChange={v => setF({ ...f, client_secret: v })} placeholder={conn.connected ? "•••• (leave blank to keep existing)" : "your client secret"} type="password" testid="conn-d365-secret"/>
        <Field label="Environment URL"     value={f.org_url}       onChange={v => setF({ ...f, org_url: v })}      placeholder="https://yourorg.crm.dynamics.com" testid="conn-d365-url"/>
      </div>
      {probe && <ProbeResult r={probe}/>}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button className="btn btn-secondary btn-sm" onClick={test}  disabled={!!busy} data-testid="conn-d365-test">{busy === "test" ? <Loader2 size={13} className="animate-spin"/> : "Test connection"}</button>
        <button className="btn btn-primary btn-sm"   onClick={save}  disabled={!!busy} data-testid="conn-d365-save">{busy === "save" ? <Loader2 size={13} className="animate-spin"/> : conn.connected ? "Update" : "Save & connect"}</button>
        {conn.connected && (
          <button className="btn btn-ghost btn-sm text-red-700" onClick={remove} disabled={!!busy} data-testid="conn-d365-delete">
            <Trash2 size={12}/> Disconnect
          </button>
        )}
        {conn.last_tested_at && (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Last tested {new Date(conn.last_tested_at).toLocaleString()} · {conn.last_status || "ok"}
          </span>
        )}
      </div>
    </div>
  );
}

function HanaCard({ conn, reload }) {
  const [f, setF] = useState({
    endpoint: conn.endpoint || "",
    username: conn.username || "",
    password: "",
  });
  const [busy, setBusy] = useState("");
  const [probe, setProbe] = useState(null);

  const save = async () => {
    if (!f.endpoint || !f.username || !f.password) return;
    setBusy("save"); setProbe(null);
    try { await csuiteApi.saveHanaConfig(f); await reload(); }
    catch (e) { setProbe({ ok: false, error: e.message }); }
    finally { setBusy(""); }
  };
  const test = async () => {
    if (!f.endpoint || !f.username || !f.password) { setProbe({ ok: false, error: "All three fields are required." }); return; }
    setBusy("test"); setProbe(null);
    try { const r = await csuiteApi.testHana(f); setProbe(r); }
    catch (e) { setProbe({ ok: false, error: e.message }); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm("Disconnect the S/4HANA tenant? Cockpit will fall back to mock data.")) return;
    setBusy("delete");
    try { await csuiteApi.deleteConnection("hana"); setF({ endpoint:"", username:"", password:"" }); await reload(); }
    finally { setBusy(""); }
  };

  return (
    <div className="card p-5" data-testid="conn-hana-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SAP S/4HANA</div>
          <div className="font-display text-lg font-extrabold">OData endpoint · Basic auth</div>
          <div className="text-xs text-zinc-500 mt-1">Enable OData for the standard Fiori catalogs (or CDS views) and provide the service endpoint + a service-user credential with read scope. HTTPS required.</div>
        </div>
        <StatusBadge conn={conn} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Endpoint URL"     value={f.endpoint} onChange={v => setF({ ...f, endpoint: v })} placeholder="https://s4hana.yourdomain.com" testid="conn-hana-endpoint"/>
        <Field label="Username"         value={f.username} onChange={v => setF({ ...f, username: v })} placeholder="INTEG_USER" testid="conn-hana-user"/>
        <Field label="Password"         value={f.password} onChange={v => setF({ ...f, password: v })} placeholder={conn.connected ? "•••• (leave blank to keep existing)" : "service password"} type="password" testid="conn-hana-pass"/>
      </div>
      {probe && <ProbeResult r={probe}/>}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button className="btn btn-secondary btn-sm" onClick={test}  disabled={!!busy} data-testid="conn-hana-test">{busy === "test" ? <Loader2 size={13} className="animate-spin"/> : "Test connection"}</button>
        <button className="btn btn-primary btn-sm"   onClick={save}  disabled={!!busy} data-testid="conn-hana-save">{busy === "save" ? <Loader2 size={13} className="animate-spin"/> : conn.connected ? "Update" : "Save & connect"}</button>
        {conn.connected && (
          <button className="btn btn-ghost btn-sm text-red-700" onClick={remove} disabled={!!busy} data-testid="conn-hana-delete">
            <Trash2 size={12}/> Disconnect
          </button>
        )}
        {conn.last_tested_at && (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Last tested {new Date(conn.last_tested_at).toLocaleString()} · {conn.last_status || "ok"}
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input type={type} className="fld py-1.5 px-2 text-sm w-full" value={value} placeholder={placeholder}
             onChange={e => onChange(e.target.value)} data-testid={testid}/>
    </label>
  );
}

function ProbeResult({ r }) {
  const ok = !!r.ok;
  return (
    <div className={`mt-3 p-3 rounded-sm text-sm flex items-start gap-2 ${ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="conn-probe-result">
      {ok ? <CheckCircle2 size={14} className="mt-0.5"/> : <XCircle size={14} className="mt-0.5"/>}
      <div>
        {ok ? (
          <div>
            <b>Connection OK</b>
            {r.organization_id && <div className="font-mono text-[11px] text-zinc-600 mt-0.5">org · {r.organization_id}</div>}
            {r.note && <div className="text-xs text-zinc-600 mt-0.5">{r.note}</div>}
          </div>
        ) : (
          <div><b>Failed</b> — {r.error}</div>
        )}
      </div>
    </div>
  );
}
